
import { useEffect, useState } from 'react';

import { Pill } from '../../components/ui';
import type { ToolTask } from '../../types/api';
import {
  PLACEMENT_STAGES,
  PLACEMENT_VIDEO_STAGES,
  placementProgress,
  videoStageIndex,
} from './usePlacement';
import styles from './placement.module.css';

const STAGE_WEIGHT: Readonly<Record<string, number>> = {
  токенизация: 1,
  'разметка якорей': 4,
  'правила плотности': 1,
  'подбор медиа': 3.5,
  'скачивание исходника': 0.3,
  'тайминги речи': 1.3,
  аудиодорожка: 0.4,
  'разметка и подбор': 3.6,
  'скачивание медиа': 0.6,
  'сборка манифеста': 0.4,
  'проверка манифеста': 0.4,
  'сборка ролика': 2.2,
  выгрузка: 0.6,
  готово: 0.6,
};

function elapsedLabel(startedAt: string | null): string | null {
  if (!startedAt) return null;
  const started = Date.parse(startedAt);
  if (!Number.isFinite(started)) return null;
  const seconds = Math.max(0, Math.round((Date.now() - started) / 1000));
  const minutes = Math.floor(seconds / 60);
  return minutes > 0 ? `идёт ${minutes} мин ${seconds % 60} с` : `идёт ${seconds} с`;
}

interface ProgressProps {
  title: string;
  stage: string | null;
  percent: number | null;
  startedAt: string | null;
  stages: readonly string[];
  indexOfStage?: (stage: string | null) => number;
}

function ProgressBar({ title, stage, percent, startedAt, stages, indexOfStage }: ProgressProps) {
  const [, tick] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => tick((value) => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const currentIndex = indexOfStage ? indexOfStage(stage) : stage ? stages.indexOf(stage) : -1;
  const elapsed = elapsedLabel(startedAt);

  return (
    <div className={styles.progressCard} role="status" aria-live="polite">
      <div className={styles.progressHead}>
        <span className={styles.pulse} />
        <span className={styles.progressTitle}>{title}</span>
        {stage && <Pill>{stage}</Pill>}
        <span className={styles.progressPct}>
          {percent === null ? (elapsed ?? 'работаем') : `${percent}%${elapsed ? ` · ${elapsed}` : ''}`}
        </span>
      </div>

      <div className={styles.phases}>
        {stages.map((name, index) => {
          const state =
            currentIndex < 0
              ? ''
              : index < currentIndex
                ? styles.phaseDone
                : index === currentIndex
                  ? styles.phaseNow
                  : '';
          return (
            <div
              key={name}
              className={`${styles.phase} ${state}`}
              style={{ flexGrow: STAGE_WEIGHT[name] ?? 1 }}
              title={name}
            />
          );
        })}
      </div>

      <div className={styles.phaseRow}>
        <span>Вкладку можно закрыть, работа не прервётся.</span>
      </div>
    </div>
  );
}

export function RunProgress({ task }: { task: ToolTask | undefined }) {
  const { stage, percent } = placementProgress(task);
  return (
    <ProgressBar
      title="Подбираем вставки"
      stage={stage}
      percent={percent}
      startedAt={task?.started_at ?? task?.created_at ?? null}
      stages={PLACEMENT_STAGES}
    />
  );
}

export function VideoRunProgress({
  stage,
  progress,
  startedAt,
}: {
  stage: string;
  progress: number;
  startedAt: string | null;
}) {
  const percent = Number.isFinite(progress)
    ? Math.max(0, Math.min(100, Math.round(progress * 100)))
    : null;
  return (
    <ProgressBar
      title="Собираем ролик со вставками"
      stage={stage || null}
      percent={percent}
      startedAt={startedAt}
      stages={PLACEMENT_VIDEO_STAGES}
      indexOfStage={videoStageIndex}
    />
  );
}
